// import { createTheme } from '@mui/material/styles';
// import { faIR } from '@mui/material/locale'; // Use Persian (Farsi) localization for RTL

// const theme = createTheme(
//   {
//     direction: 'rtl', // Enable Right-to-Left layout
//     palette: {
//       primary: {
//         main: '#FFFFFFD9', // Header color
//       },
//       background: {
//         default: '#FFFFFFD9', // Main background color
//       },
//     },
//     typography: {
//       fontFamily: 'IRANYekanXFaNum, Arial, sans-serif', // Custom font for Farsi/Arabic
//     },
//   },
//   faIR // Apply Persian (Farsi) localization
// );

// export default theme;

import { createTheme } from '@mui/material/styles';

/**
 * Design tokens — the single source of truth for spacing and corner radius.
 *
 * NOTE: the theme keeps `spacing: 1`, so every MUI spacing number in this app
 * is a raw pixel value (`padding: 16` === 16px), not the usual 8px step.
 * Use these tokens instead of literals so the scale stays consistent.
 */
export const SPACING = {
  xs: 4,
  sm: 8,
  md: 16, // default gap between cards / section padding
  lg: 24,
  xl: 32,
} as const;

export const RADIUS = {
  sm: 4,
  md: 8, // default for cards and panels
  lg: 12,
  pill: 999,
} as const;

/** Brand blue: spinners, progress indicators, and the selected/active state. */
export const BRAND_BLUE = '#4994ec';

/**
 * Primary action colour — the deep indigo used by the auth screens' main
 * button. Distinct from BRAND_BLUE, which marks state (active/selected)
 * rather than "this is the thing to press".
 */
export const ACTION_BLUE = '#2C3EDB';
export const ACTION_BLUE_HOVER = '#2333B8';

/** Solid primary button: use for the main action of a form or dialog. */
export const filledButtonSx = {
  backgroundColor: ACTION_BLUE,
  color: '#FFFFFF',
  textTransform: 'none',
  borderRadius: `${RADIUS.md}px`,
  fontFamily: 'IRANYekanXFaNum',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { backgroundColor: ACTION_BLUE_HOVER, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#B0BEC5', color: '#FFFFFF' },
} as const;

/**
 * Shared look for the outlined toolbar/action buttons: white by default,
 * solid brand blue with white content on hover.
 *
 * Spread it into a styled() definition or an `sx` prop. Child labels and
 * icons usually carry their own inline colours, so those are overridden here
 * (including `<img>` icons, which are tinted with a CSS filter).
 */
/**
 * Tints a black/greyscale <img> icon to BRAND_BLUE (#1E88E5). The button icons
 * are <img> tags, not inline svg, so `color`/`fill` cannot reach them — and
 * they ship with different hardcoded colours (arrow-right-circle.svg strokes
 * #A6AAB5, edit-square.svg fills #1E88E5), which is why the two buttons looked
 * mismatched at rest. `brightness(0)` flattens whatever colour the file has to
 * black first, so the result is identical regardless of the source.
 */
export const BRAND_BLUE_IMG_FILTER =
  'brightness(0) saturate(100%) invert(45%) sepia(85%) saturate(1900%) hue-rotate(187deg) brightness(94%) contrast(89%)';

/** Same trick as above, tuned to ACTION_BLUE (#2C3EDB). */
export const ACTION_BLUE_IMG_FILTER =
  'brightness(0) saturate(100%) invert(19%) sepia(86%) saturate(4500%) hue-rotate(240deg) brightness(93%) contrast(95%)';

export const outlinedButtonSx = {
  color: ACTION_BLUE,
  backgroundColor: '#FFFFFF',
  border: '1px solid #C6C6DF',
  transition:
    'background-color .2s ease, border-color .2s ease, color .2s ease',
  cursor: 'pointer',
  // Normalise the resting colour of <img> icons across every button using this.
  '& img': {
    filter: ACTION_BLUE_IMG_FILTER,
    transition: 'filter .2s ease',
  },
  '&:hover': {
    backgroundColor: ACTION_BLUE,
    borderColor: ACTION_BLUE,
    color: '#FFFFFF',
    '& p, & span, & a': { color: '#FFFFFF !important' },
    // Inline SVGs (MUI icons and svgr imports).
    '& svg': { color: '#FFFFFF !important', fill: '#FFFFFF' },
    '& svg path, & svg circle, & svg rect, & svg line, & svg polyline': {
      stroke: 'currentColor',
    },
    // <img> icons are tinted with a CSS filter, so force them to pure white.
    '& img': { filter: 'brightness(0) invert(1) !important' },
  },
} as const;

const theme = createTheme({
  palette: {
    primary: {
      main: '#FFFFFFD9',
    },
    secondary: {
      main: '#1E88E5',
    },
    error: {
      main: '#f44336',
    },
    // Brand blue used for spinners/progress. `primary` is near-white here,
    // so it cannot serve that role.
    info: {
      main: BRAND_BLUE,
    },
    text: {
      primary: '#25282E',
      secondary: '##E5E5E8',
    },
    background: {
      // Page background is a light grey so white cards read as separate
      // surfaces; a white page made the gaps between them invisible.
      default: '#F4F4F6',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: 'IRANYekanXFaNum, sans-serif',
    fontSize: 14,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 900,
  },
  // 1 => MUI spacing numbers are plain pixels across this app.
  // Changing this would rescale every existing padding/margin, so it stays 1
  // and the SPACING tokens above carry the scale instead.
  spacing: 1,
  shape: {
    borderRadius: RADIUS.md,
  },
  components: {
    // palette.primary.main is near-white here, so an unstyled spinner is
    // barely visible. Default every CircularProgress to the brand blue;
    // components that pass their own `color`/`sx` still win.
    MuiCircularProgress: {
      defaultProps: {
        color: 'info',
      },
    },
    // Every solid button gets the primary action colour from one place.
    // Individual `sx` overrides still win, so a component that genuinely
    // needs another colour (error/destructive) is unaffected.
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: ACTION_BLUE,
          color: '#FFFFFF',
          textTransform: 'none',
          boxShadow: 'none',
          fontFamily: 'IRANYekanXFaNum',
          '&:hover': {
            backgroundColor: ACTION_BLUE_HOVER,
            boxShadow: 'none',
          },
        },
      },
    },
  },
});

export default theme;
